import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function AttendanceLogs() {
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ event_id: "", start: "", end: "" });

  const load = async () => {
    try {
      const params = {};
      if (filters.event_id) params.event_id = filters.event_id;
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const { data } = await api.get("/attendance", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <div data-testid="logs-page">
      <PageHeader eyebrow="Org Admin" title="Attendance logs" />

      <div className="border border-[#D2D8D4] rounded-md bg-white p-4 mb-6 grid md:grid-cols-4 gap-3 items-end">
        <Field label="Event">
          <select
            value={filters.event_id}
            onChange={(e) => setFilters({ ...filters, event_id: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
            data-testid="logs-event-filter"
          >
            <option value="">All events</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Start">
          <input type="date" value={filters.start}
            onChange={(e) => setFilters({ ...filters, start: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="logs-start" />
        </Field>
        <Field label="End">
          <input type="date" value={filters.end}
            onChange={(e) => setFilters({ ...filters, end: e.target.value })}
            className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="logs-end" />
        </Field>
        <button onClick={() => setFilters({ event_id: "", start: "", end: "" })}
          className="px-4 py-2 rounded-md border border-[#D2D8D4] font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors"
          data-testid="logs-clear">
          Clear
        </button>
      </div>

      <div className="border border-[#E4E4E7] bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#F7F7F8] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Member ID</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Member</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Event</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Time</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No attendance logs yet.</td></tr>
            )}
            {rows.map((r) => {
              const s = (r.attendance_status || "on_time").toLowerCase();
              return (
                <tr key={r.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                  <td className="px-4 py-3 mono font-semibold text-[#05126E]">{r.member_code || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{r.member_name}</td>
                  <td className="px-4 py-3">{r.event_name}</td>
                  <td className="px-4 py-3 mono text-zinc-700">
                    {new Date(r.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${
                        s === "late"
                          ? "bg-[#FFF3CD] text-[#7a5b00] border border-[#C9A227]/40"
                          : "bg-[#E6E9F5] text-[#05126E] border border-[#05126E]/30"
                      }`}
                      data-testid={`status-${r.id}`}
                    >
                      {s === "late" ? "Late" : "On time"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
